import cx from "classnames";
import React, { useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

import useIsClient from "utils/useIsClient";

import CrossIcon from "./cross.svg";
import styles from "./modal.styles.module.scss";

interface ModalComponentProps {
  isOpen?: boolean;
  children?: React.ReactNode;
  onClose?: () => void;
  boxChildren?: React.ReactNode;
}

export function ModalComponent({
  isOpen,
  children,
  onClose,
  boxChildren,
}: ModalComponentProps) {
  const isClient = useIsClient();

  const initialRender = useRef(true);

  const $root = isClient ? document.getElementById("modal-root") : null;

  const open = useCallback(() => {
    document.body.style.top = `-${window.scrollY}px`;
    document.body.classList.add("modal-open");
  }, []);

  const close = useCallback(() => {
    const scrollTop = parseInt(
      document.body.style.top?.replace("-", "")?.replace("px", ""),
      10,
    );
    document.body.classList.remove("modal-open");
    document.body.style.top = "";
    if (!isNaN(scrollTop)) window.scrollTo({ top: scrollTop });
  }, []);

  // toggle class on body element
  useEffect(() => {
    if (isOpen) {
      open();
    } else if (!initialRender.current) {
      close();
    }

    initialRender.current = false;
  }, [isOpen, initialRender, open, close]);

  // handle escape press
  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && onClose && isOpen) onClose();
    },
    [onClose, isOpen],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleKeyPress, onClose]);

  // force remove `.modal-open` on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => close(), []);

  if (!$root) {
    if (isClient) {
      // eslint-disable-next-line no-console
      console.warn("[ModalComponent] could not find any $root element");
    }
    return <noscript />;
  }

  if (!isOpen) {
    return <noscript />;
  }

  return ReactDOM.createPortal(
    <div
      role="button"
      tabIndex={0}
      className={styles.container}
      onClick={onClose}
    >
      <div
        role="button"
        tabIndex={0}
        className={styles.box}
        onClick={(event) => event.stopPropagation()}
      >
        {boxChildren}
        <div className={styles.content}>
          <div
            role="button"
            tabIndex={0}
            className={cx(
              "justify-center items-center absolute top-0 right-0 cursor-pointer z-50 w-10 h-10 lg:w-14 lg:h-14",
              onClose === undefined ? "hidden" : "flex",
            )}
            data-cy="close-modal"
            onClick={onClose}
          >
            <CrossIcon />
          </div>
          {children}
        </div>
      </div>
    </div>,
    $root,
  );
}
